import { isEqual } from "lodash"

class ProductFilter {
  type
  _id
  name
  info
  order

  initialShown = false

  _parentMarketSelected = false
  _parentEnumsSelected = false
  _parentMarketId = undefined
  _parentEnumParams
  _marketFilter
  _parentMarketFilterRegistered = false
  _parentEnumFiltersRegistered = false
  _allValuesEqual = false

  _hasEffect = true
  _isActive = false

  constructor(group, type) {
    this._id = group.strapiId
    this.name = group.name
    this.info = group.info
    this.order = group.order || 0
    this.initialShown = !!group.initialShown
    if (group.parent_market) {
      this._parentMarketId = group.parent_market.id
    }
    this._parentEnumParams = group.parent_enum_params || []
    this.type = type
  }

  isHiddenByParent() {
    return (
      (this._parentMarketId && !this._parentMarketSelected) ||
      (this._parentEnumParams.length > 0 && !this._parentEnumsSelected)
    )
  }

  /**
   * returns if the entire filter should be hidden.
   * @returns {boolean}
   */
  isHidden() {
    return (
      this.isHiddenByParent() ||
      (!this._hasEffect && !this._isActive) ||
      (this._allValuesEqual && !this._isActive)
    )
  }

  isActive() {
    return this._isActive
  }

  matches() {
    return true
  }

  /**
   * This function returns if a product could be affected by the filter in any state.
   * @param product A product to test.
   */
  hasEffectOn(product) {
    return true
  }

  reset() {
    this._isActive = false
  }

  externalReset() {
    this.reset()
    this._updated(false)
  }

  _beforeUpdateFns = []
  _updateFns = []

  /**
   * Register a function to the beforeUpdate callback. If the function returns a truthy value the update gets canceled.
   * @param fn beforeUpdate callback function.
   */
  onBeforeUpdate(fn) {
    this._beforeUpdateFns.push(fn)
  }

  onUpdate(fn) {
    this._updateFns.push(fn)
  }

  _updated(active = true) {
    this._isActive = active

    let shouldUpdate = true
    for (const updateFn of this._beforeUpdateFns) {
      shouldUpdate = shouldUpdate && !updateFn()
    }

    if (shouldUpdate) {
      for (const updateFn of this._updateFns) {
        updateFn()
      }
    }
  }

  destroy() {
    this._updateFns = []
    this._beforeUpdateFns = []
  }

  /**
   * This function compares the value of the product with the same id as the filter.
   * @returns 0 if the params are equal, <0 if p1<p2, >0 if p1>p2
   */
  compareProducts(p1, p2) {
    return -10
  }

  _parentMarketFilterUpdated(selectedMarkets = []) {
    let parentSelected =
      selectedMarkets.findIndex((m) => m.id === this._parentMarketId) !== -1

    if (!parentSelected && this._isActive && !this._parentEnumsSelected) {
      this.reset()
      this._updated(false)
    }

    this._parentMarketSelected = parentSelected
  }

  _parentEnumFilterUpdated(filter, requiredIds = []) {
    let parentSelected = false
    for (const requiredId of requiredIds) {
      let values = []
      if (filter) {
        values = filter._getSelected()
      }
      if (values.findIndex((item) => item._valueId === requiredId) !== -1) {
        parentSelected = true
        break
      }
    }

    if (!parentSelected && this._isActive && !this._parentMarketSelected) {
      this.reset()
      this._updated(false)
    }

    this._parentEnumsSelected = parentSelected
  }

  /**
   * This function is called if the products list updates.
   * @param products new list of products
   * @param filteredProductsWithoutCurrent list of products not which is not affected by the current filter
   * @param enumFilters all enumProductFilters
   * @param preselectedMarkets an array of preselected markets. overwrites the marketFilter
   */
  updateValues(
    products,
    filteredProductsWithoutCurrent = [],
    enumFilters = [],
    preselectedMarkets,
  ) {
    // update parent market
    if (this._parentMarketId) {
      if (preselectedMarkets) {
        this._parentMarketFilterUpdated(preselectedMarkets)
      } else {
        const marketFilter = enumFilters.find((f) => f._id === -2)
        if (!this._marketFilter && marketFilter) {
          if (marketFilter.selected) {
            this._marketFilter = marketFilter
            this._marketFilter.onBeforeUpdate(() => {
              this._parentMarketFilterUpdated(this._marketFilter._getSelected())
              return false
            })
          }
        }

        let val = []
        if (this._marketFilter) {
          val = this._marketFilter._getSelected()
        }
        this._parentMarketFilterUpdated(val)
      }
    }

    // update parent enum filters
    const parentFilters = {}

    for (const parentEnumParam of this._parentEnumParams) {
      if (!parentFilters[parentEnumParam.product_parameter_group]) {
        parentFilters[parentEnumParam.product_parameter_group] = []
      }
      parentFilters[parentEnumParam.product_parameter_group].push(
        parentEnumParam.id,
      )
    }

    for (const parentFiltersKey in parentFilters) {
      const parentFilter = enumFilters.find((f) => +f._id === +parentFiltersKey)
      if (parentFilter && !this._parentEnumFiltersRegistered) {
        this._parentEnumFiltersRegistered = true
        parentFilter.onBeforeUpdate(() => {
          this._parentEnumFilterUpdated(
            parentFilter,
            parentFilters[parentFiltersKey],
          )
          return false
        })
      }
      this._parentEnumFilterUpdated(
        parentFilter,
        parentFilters[parentFiltersKey],
      )
    }

    // update has effect
    let hasEffect = false
    for (let i = 0; i < products.length; i++) {
      if (this.hasEffectOn(products[i])) {
        hasEffect = true
        break
      }
    }

    this._hasEffect = hasEffect

    // check if all values of this parameter are the same
    if (filteredProductsWithoutCurrent.length > 1) {
      const sorted = filteredProductsWithoutCurrent.sort((p1, p2) =>
        this.compareProducts(p1, p2),
      )
      this._allValuesEqual =
        this.compareProducts(sorted[0], sorted[sorted.length - 1]) === 0
    } else {
      this._allValuesEqual = true
    }
  }

  _indexOf(values, value) {
    return values.findIndex((val) => isEqual(val, value))
  }

  _includesValue(values, value) {
    return this._indexOf(values, value) !== -1
  }

  getState() {
    return {
      _id: this._id,
      _isActive: this._isActive,
      _parentEnumsSelected: this._parentEnumsSelected || true,
      _parentMarketSelected: this._parentMarketSelected || true,
    }
  }

  restoreState(state) {
    this._isActive = state._isActive
    this._parentEnumsSelected = state._parentEnumsSelected
    this._parentMarketSelected = state._parentMarketSelected
  }
}

export default ProductFilter
